import { create } from '@thoughtspot/logger';
import {
    clearSessionInfo,
    getImpersonationInfo,
    getOnUserLoggedOut,
    getUserDisplayName,
    isOrgPerUrlEnabled,
    isPartitionedCookieEnabled,
    markManuallyLoggedOut,
    setSessionInfo,
    shouldLogoutWithRedirect,
} from '@thoughtspot/session-service';
import {
    getOidcLoginUriFromIssuers,
    isOidcEnabled,
    isOktaEnabled,
} from '@thoughtspot/system-config-service';
import React from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import { useDoLogout } from '/@services/system/login-service';
import { ImpersonationIndicator } from './impersonation-indicator';

const logger = create('impersonation-indicator.container');

/**
 * Strips an `orgId` override query param from a URL. Mirrors the helper used
 * by the user menu's sign-out flow so org-per-URL logout returns to the base
 * (non-org-overridden) login URL.
 */
export const removeOverrideOrgId = (url: string): string => {
    if (!url.includes('orgId')) {
        return url;
    }
    const [baseUrl, hash] = url.split('#');
    const [basePath, queryString] = baseUrl.split('?');
    const queryStringWithoutOrgOverride = (queryString ?? '').replace(
        /(?:&|^)orgId=[^&]*/,
        '',
    );
    return `${basePath}${
        queryStringWithoutOrgOverride ? `?${queryStringWithoutOrgOverride}` : ''
    }${hash ? `#${hash}` : ''}`;
};

/**
 * Wires the impersonation ("act as") indicator to the session: reads the
 * impersonated user and the session expiry from session info, and ends the
 * session on exit. Renders nothing outside an impersonated session.
 *
 * Lives in a container because ending the session needs the logout service
 * and app context, which presentational components must not depend on.
 */
export const ImpersonationIndicatorContainer: React.FC = () => {
    const { doLogout } = useDoLogout();
    const { navService } = useAppContext();
    const impersonationInfo = getImpersonationInfo();

    const exitImpersonation = async () => {
        // Exiting impersonation (manually or when the timer reaches 0) ends
        // the acting-as session by logging out and returning to the login
        // screen. doLogout() alone only blanks the app, so we mirror the user
        // menu's sign-out (js/ts-apps/coms/.../user-menu.container.tsx),
        // including the OKTA/IAMv2 and org-per-URL redirect branches.
        // TODO(SCAL-325654): swap for a dedicated exit-impersonation API if
        // one is introduced. Ideally extract a shared logout helper so this
        // stays in sync with the user menu instead of duplicating it.
        const issuerLink = getOidcLoginUriFromIssuers();
        const shouldDoIAMv2Logout =
            !!issuerLink && isOidcEnabled() && isOktaEnabled();

        // OKTA with non-partitioned cookies: hard-redirect to OKTA sign-in
        // (no local logout mutation / soft /login redirect).
        if (shouldDoIAMv2Logout && !isPartitionedCookieEnabled()) {
            markManuallyLoggedOut();
            const { origin } = new URL(issuerLink);
            // eslint-disable-next-line no-restricted-syntax
            window.location.replace(`${origin}/signin?clearSession=true`);
            return;
        }

        try {
            await doLogout();
        } catch (error) {
            // A failed logout mutation must not strand the admin inside the
            // impersonated session, so log it and continue with the local
            // teardown and redirect (as login-service's authenticate() does).
            logger.error(
                'Logout mutation failed while exiting impersonation',
                error,
            );
        }
        setSessionInfo(null);
        clearSessionInfo();
        if (!shouldDoIAMv2Logout) {
            // Not needed for IAM v2 — would break the partitioned-cookie
            // redirect. Publishing onUserLoggedOut drives goToLogin().
            markManuallyLoggedOut();
            getOnUserLoggedOut().publish();
        }

        if (isOrgPerUrlEnabled() && window.location.href.includes('orgId')) {
            const currentPathBeforeHash = `${
                window.location.href.split('#')[0]
            }#`;
            // eslint-disable-next-line no-restricted-syntax
            window.location.replace(removeOverrideOrgId(currentPathBeforeHash));
            navService.goToLogin();
        } else if (shouldDoIAMv2Logout && shouldLogoutWithRedirect()) {
            // Clear the OKTA session and come back to this host.
            const { origin } = new URL(issuerLink);
            const targetUrl = encodeURIComponent(
                `https://${window.location.hostname}`,
            );
            // eslint-disable-next-line no-restricted-syntax
            window.location.replace(
                `${origin}/signin?clearSession=true&targetUrl=${targetUrl}`,
            );
        } else {
            navService.goToLogin();
        }
    };

    /**
     * The indicator calls this from a click handler and from the expiry timer,
     * neither of which can await it — so nothing is left to reject unhandled:
     * anything that escapes the exit flow is logged, and the admin is still
     * sent to the login screen rather than left in the impersonated session.
     */
    const handleExitImpersonation = () => {
        exitImpersonation().catch(error => {
            logger.error('Failed to exit the impersonation session', error);
            navService.goToLogin();
        });
    };

    if (!impersonationInfo?.isImpersonated) {
        return null;
    }

    return (
        <ImpersonationIndicator
            userDisplayName={getUserDisplayName()}
            onExitSession={handleExitImpersonation}
            sessionEndTime={impersonationInfo.sessionEndTime}
        />
    );
};
